import React from 'react';
import {
  arrayOf, bool, func, number, shape, string, objectOf
} from 'prop-types';
import { Typography, TextField, InputAdornment, IconButton } from '@one-thd/sui-atomic-components';
import { Close } from '@one-thd/sui-icons';

const MIN_AREA_NUMBER = 1;

export const CalculatorInputElement = ({
  inputType,
  errorBox,
  index,
  areas,
  showDescriptions,
  lineItemName,
  calculatorType,
  dimensionNames,
  step,
  handleInputChange,
  removeInputElement,
  hideAreaText
}) => {
  const inputClasses = 'sui-mr-3';

  const dimensions = (dimensionNames && dimensionNames.length !== 0)
    ? dimensionNames.reduce((names, currentDimension, i) => {
      const dimensionList = names;
      dimensionList['dimension' + (i + 1)] = currentDimension;
      return dimensionList;
    }, {})
    : { dimension1: 'Length', dimension2: 'Width' };

  return (
    <div key={index} className="sui-my-2">
      <div className="sui-flex sui-items-center">
        {!hideAreaText && (
          <div className="sui-mr-4">
            <Typography weight="bold">{`${lineItemName} ${index + 1}`}</Typography>
          </div>
        )}
        {step > MIN_AREA_NUMBER
          && (
            <IconButton icon={Close} color="brand" onClick={() => removeInputElement(index, inputType)} />
          )}
      </div>
      <div className="sui-flex sui-flex-col sm:sui-flex-row sui-mt-1">
        {showDescriptions && (
          <div className={inputClasses}>
            <TextField
              fullWidth
              label="Description:"
              name="description"
              onChange={(event) => handleInputChange(event, index, false, inputType)}
              value={areas[index].name}
            />
          </div>
        )}
        {inputType === 'lengthWidth' && (
          <>
            <div className={inputClasses}>
              <TextField
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">ft</InputAdornment>
                  )
                }}
                label={`${dimensions.dimension1}:`}
                onChange={(event) => handleInputChange(event, index, true, inputType)}
                type="number"
                value={areas[index].length}
                status={errorBox.heightLengthError ? 'error' : null}
              />
            </div>
            <div className={inputClasses}>
              <TextField
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">ft</InputAdornment>
                  )
                }}
                label={`${dimensions.dimension2}:`}
                onChange={(event) => handleInputChange(event, index, false, inputType)}
                type="number"
                value={areas[index].width}
                status={errorBox.widthError ? 'error' : null}
              />
            </div>
          </>
        )}
        {inputType === 'area' && (
          <div data-testid="calculator-input" className={inputClasses}>
            <TextField
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">sq. ft</InputAdornment>
                )
              }}
              label="Square Footage"
              onChange={(event) => handleInputChange(event, index, false, inputType)}
              type="number"
              value={areas[index].squareFootage.value}
              status={errorBox.areaError ? 'error' : null}
            />
          </div>
        )}
        {calculatorType === 'Volume' && (
          <div className={inputClasses}>
            <TextField
              fullWidth
              helpMessage="3 in. recommended"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">in</InputAdornment>
                )
              }}
              label="Depth:"
              name="depth"
              onChange={(event) => handleInputChange(event, index, false, inputType)}
              type="number"
              value={areas[index].depth}
            />
          </div>
        )}
      </div>
    </div>
  );
};

CalculatorInputElement.propTypes = {
  inputType: string.isRequired,
  index: number.isRequired,
  errorBox: shape({
    widthError: bool,
    heightLengthError: bool,
    areaError: bool
  }).isRequired,
  areas: arrayOf(shape({
    key: string,
    name: string,
    length: string,
    width: string,
    depth: string,
    squareFootage: shape({
      value: string,
      changed: bool
    })
  })).isRequired,
  step: number.isRequired,
  lineItemName: string,
  calculatorType: string,
  dimensionNames: arrayOf(string),
  handleInputChange: func.isRequired,
  removeInputElement: func.isRequired,
  showDescriptions: bool,
  hideAreaText: bool
};

CalculatorInputElement.defaultProps = {
  lineItemName: '',
  calculatorType: 'Area',
  dimensionNames: ['Width', 'Height'],
  showDescriptions: false,
  hideAreaText: false
};
